import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Info, Lock, X } from 'lucide-react';
import { DashboardHeader } from '../Dashboard/DashboardUi';
import { Modal } from '../ui/Modal';
import { Field, Segmented, Textarea } from '../ui/Field';
import { useUndo } from '../ui/UndoBar';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { useAuth } from '../../context/AuthContext';
import { FAMILLES_MODULES } from '../../../shared/catalogueModules.js';
import { messageErreur, organisationApi, type ContexteOrganisation, type ModuleCatalogue, type ServiceFiche } from '../../lib/organisationApi';
import { estModulePayant, libelleTarifModule } from '../../lib/abonnementsApi';
import { CarteModule } from './CarteModule';
import { MaFormuleCarte } from './MaFormule';
import { DroitsUsagePage } from './DroitsUsagePage';
import { EtatEchec, Squelettes } from './EtatsPage';
import { useOrganisation } from './OrganisationContext';

/**
 * « Mes modules » (maquette L01-05 à L01-08, L01-11) : grille de cartes-modules groupées par famille.
 * Un module activé apparaît dans la barre latérale du service choisi ; le désactiver ne supprime rien.
 * Sur téléphone : consultation seule (« L'activation d'un module se fait depuis un ordinateur »).
 * `clientId` : vue super-admin (L01-10) — le serveur lit alors le compte de ce client.
 */

interface Props { clientId?: string; bandeau?: React.ReactNode }

const useMobile = () => {
    const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const on = () => setMobile(mq.matches);
        mq.addEventListener('change', on);
        return () => mq.removeEventListener('change', on);
    }, []);
    return mobile;
};

/** Prévient la barre latérale qu'un module vient d'être activé ou désactivé. */
const annoncerChangement = () => window.dispatchEvent(new Event('iamani:modules-changed'));


const ModaleActivation: React.FC<{ module: ModuleCatalogue | null; service: ServiceFiche | null; etablissement: string; envoi: boolean; erreur: string; onClose: () => void; onActiver: () => void }> = ({ module: m, service, etablissement, envoi, erreur, onClose, onActiver }) => (
    <Modal
        open={!!m} onClose={onClose} size="md" title={m ? `Activer « ${m.label} »` : ''} subtitle={service ? `Pour le service ${service.name} · ${etablissement}` : undefined}
        footer={<><button type="button" className={pillSecondary(36)} onClick={onClose}>Annuler</button><button type="button" className={pillPrimary(36)} disabled={envoi} onClick={onActiver}>Activer le module</button></>}
    >
        {m && (
            <div className="space-y-4 text-[13px]">
                <section>
                    <h3 className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Ce qu'il apporte</h3>
                    <p className="text-content">{m.description}.</p>
                </section>
                <section>
                    <h3 className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Ce qu'il demande</h3>
                    {m.prerequis.length === 0 ? <p className="text-content-muted">Aucun prérequis.</p> : (
                        <ul className="space-y-1.5">
                            {m.prerequis.map((p) => (
                                <li key={p.name} className="flex items-center gap-2 text-content">
                                    {p.ok ? <Check size={14} className="text-state-ok" /> : <X size={14} className="text-state-warn" />}
                                    <span>{p.label} <span className="text-content-muted">· {p.ok ? 'activé' : p.bientot ? 'bientôt disponible' : 'manquant'}</span></span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {m.prerequis.some((p) => !p.ok) && <p className="mt-2 text-state-warn">Attention : ce module a besoin de {m.prerequis.filter((p) => !p.ok).map((p) => `« ${p.label} »`).join(', ')} pour fonctionner.</p>}
                    {(m.bon_avec || []).length > 0 && <p className="mt-2 text-content-muted">Va bien avec : {(m.bon_avec || []).map((b) => b.label).join(', ')} (facultatif).</p>}
                </section>
                <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-surface-soft px-3 py-2 text-content-muted" aria-disabled>
                    <span className="text-[10px] font-bold uppercase tracking-[.09em]">Coût</span><span>Tarif défini à la fin</span>
                </div>
                {erreur && <p role="alert" className="text-state-danger">{erreur}</p>}
            </div>
        )}
    </Modal>
);

const ModaleDemande: React.FC<{ module: ModuleCatalogue | null; service: ServiceFiche | null; etablissement: string; envoi: boolean; erreur: string; message: string; prixLu: boolean; onPrixLu: (v: boolean) => void; onMessage: (v: string) => void; onClose: () => void; onEnvoyer: () => void; onPayer: () => void }> = ({ module: m, service, etablissement, envoi, erreur, message, prixLu, onPrixLu, onMessage, onClose, onEnvoyer, onPayer }) => (
    <Modal
        open={!!m} onClose={onClose} size="md" title={m ? `Demander l’activation de « ${m.label} »` : ''} subtitle={service ? `Pour le service ${service.name} · ${etablissement}` : undefined}
        footer={<><button type="button" className={pillSecondary(36)} onClick={onClose}>Annuler</button>{m?.tarif?.mode === 'fixe' && m.tarif.prix_eur > 0 && <button type="button" className={pillPrimary(36)} disabled={envoi || !prixLu} title={prixLu ? undefined : 'Cochez d’abord la case du prix'} onClick={onPayer}>Payer {m.tarif.prix_eur.toFixed(2).replace('.', ',')} € et activer maintenant</button>}<button type="button" className={m?.tarif?.mode === 'fixe' && m.tarif.prix_eur > 0 ? pillSecondary(36) : pillPrimary(36)} disabled={envoi} onClick={onEnvoyer}>Envoyer la demande</button></>}
    >
        {m && (
            <div className="space-y-4 text-[13px]">
                <p className="text-content">{m.description}.</p>
                {estModulePayant(m.tarif) ? (
                    <div className="rounded-lg border border-state-warn/40 bg-state-warn/10 p-3 space-y-2" role="group" aria-label="Prix du module">
                        <p className="text-content"><b>Ce module est payant : {libelleTarifModule(m.tarif)}, hors taxes.</b></p>
                        <p className="text-content-muted">{m.tarif?.mode === 'paliers' ? 'Le prix exact dépend du palier que vous choisirez avec L’IAmani. ' : ''}Il sera facturé à partir du mois suivant son activation. Rien n’est débité en envoyant cette demande.</p>
                        <label className="flex items-start gap-2 text-content font-semibold">
                            <input type="checkbox" className="mt-1" checked={prixLu} onChange={(e) => onPrixLu(e.target.checked)} />
                            <span>J’ai pris connaissance du prix et je comprends que ce module sera facturé.</span>
                        </label>
                    </div>
                ) : (
                    <p className="text-content-muted">Ce module est gratuit pour le moment. Vous ne payez rien en envoyant cette demande.</p>
                )}
                {m?.tarif?.mode === 'fixe' && m.tarif.prix_eur > 0 && <p className="text-content-muted"><b>Pressé ?</b> « Payer et activer maintenant » règle le premier mois par carte, active le module aussitôt, et ce montant est déduit de votre prochaine facture.</p>}
                <p className="text-content-muted">L’IAmani lit votre demande et vous répond : le module apparaît dans votre barre latérale dès qu’elle est acceptée.</p>
                <Field label="Un mot pour L’IAmani (facultatif)" counter={`${message.length}/300`}>
                    {(ids) => <Textarea {...ids} value={message} maxLength={300} rows={3} onChange={(e) => onMessage(e.target.value)} placeholder="Par exemple : nous en avons besoin pour le service du soir." />}
                </Field>
                {erreur && <p role="alert" className="text-state-danger">{erreur}</p>}
            </div>
        )}
    </Modal>
);

export const MesModulesPage: React.FC<Props> = ({ clientId, bandeau }) => {
    const { user } = useAuth();
    const undo = useUndo();
    const { recharger } = useOrganisation();
    const mobile = useMobile();
    const [contexte, setContexte] = useState<ContexteOrganisation | null>(null);
    const [serviceId, setServiceId] = useState<string | null>(null);
    const [service, setService] = useState<ServiceFiche | null>(null);
    const [modules, setModules] = useState<ModuleCatalogue[] | null>(null);
    const [erreur, setErreur] = useState<unknown>(null);
    const [famille, setFamille] = useState<string>('Toutes');
    const [aActiver, setAActiver] = useState<ModuleCatalogue | null>(null);
    const [aDemander, setADemander] = useState<ModuleCatalogue | null>(null);
    const [motDemande, setMotDemande] = useState('');
    const [prixLu, setPrixLu] = useState(false);
    const [confirmation, setConfirmation] = useState('');
    const [envoi, setEnvoi] = useState(false);
    const [erreurModale, setErreurModale] = useState('');
    const [alerte, setAlerte] = useState('');
    const [incline, setIncline] = useState<string | null>(null);
    const [aide, setAide] = useState(true);
    const minuteur = useRef<number | undefined>(undefined);

    const charger = useCallback(async (id?: string | null) => {
        setErreur(null);
        try {
            const ctx = contexte || await organisationApi.contexte(clientId);
            setContexte(ctx);
            const cible = id || serviceId || ctx.services[0]?.id;
            if (!cible) { setModules([]); return; }
            setServiceId(cible);
            const r = await organisationApi.modules(cible, clientId);
            setService(r.service); setModules(r.modules);
        } catch (e) { setErreur(e); setModules(null); }
    }, [clientId, contexte, serviceId]);
    // Retour de la page de paiement Stripe d'un module (?paiement=ok|attente|annule|echec).
    useEffect(() => {
        const r = new URLSearchParams(window.location.search).get('paiement');
        if (!r) return;
        setConfirmation(r === 'ok' ? 'Paiement reçu : votre module est activé. Le montant sera déduit de votre prochaine facture.' : r === 'attente' ? 'Paiement en cours de confirmation : le module s’activera dès qu’il sera confirmé.' : r === 'annule' ? 'Paiement annulé : rien n’a été débité.' : 'Le paiement n’a pas abouti.');
        window.history.replaceState(null, '', window.location.pathname);
    }, []);

    useEffect(() => { void charger(); /* chargement initial */ // eslint-disable-line react-hooks/exhaustive-deps
    }, [clientId]);
    useEffect(() => () => window.clearTimeout(minuteur.current), []);

    const nbActifs = modules?.filter((m) => m.etat === 'actif').length || 0;
    const groupes = useMemo(() => {
        const liste = (modules || []).filter((m) => famille === 'Toutes' || m.famille === famille);
        return (FAMILLES_MODULES as string[]).map((f) => ({ titre: f, cartes: liste.filter((m) => m.famille === f) })).filter((g) => g.cartes.length > 0);
    }, [modules, famille]);

    const remplacer = (m: ModuleCatalogue) => setModules((liste) => (liste || []).map((x) => (x.name === m.name ? m : x)));
    // Après une activation ou une désactivation : la barre latérale et le sélecteur se mettent à jour (sauf vue super-admin).
    const apresBascule = () => { annoncerChangement(); if (!clientId) void recharger(); };

    const activer = async (m: ModuleCatalogue) => {
        if (!serviceId) return;
        setEnvoi(true); setErreurModale('');
        try {
            const r = await organisationApi.basculerModule(serviceId, m.name, true, clientId);
            remplacer(r.module); setAActiver(null);
            // Inclinaison de départ, puis la carte se pose (transition de 420 ms posée par la carte).
            setIncline(m.name);
            window.clearTimeout(minuteur.current);
            minuteur.current = window.setTimeout(() => setIncline(null), 40);
            apresBascule();
        } catch (e) { setErreurModale(messageErreur(e)); }
        finally { setEnvoi(false); }
    };

    const desactiver = async (m: ModuleCatalogue) => {
        if (!serviceId) return;
        setAlerte('');
        try {
            const r = await organisationApi.basculerModule(serviceId, m.name, false, clientId);
            remplacer(r.module);
            apresBascule();
            undo.push({
                message: `« ${m.label} » désactivé. Vos données sont conservées : réactivez le module pour les retrouver.`,
                seconds: 10,
                undoneMessage: `« ${m.label} » réactivé`,
                undo: async () => {
                    try {
                        const back = await organisationApi.basculerModule(serviceId, m.name, true, clientId);
                        remplacer(back.module); apresBascule();
                    } catch (e) { setAlerte(messageErreur(e)); }
                },
            });
        } catch (e) { setAlerte(messageErreur(e)); }
    };

    // Seul le super-admin active ou désactive un module ; la direction voit l'état de ses modules et peut en demander l'activation.
    const peutBasculer = contexte?.role === 'admin';
    const peutDemander = contexte?.role === 'direction';

    const payer = async (m: ModuleCatalogue) => {
        if (!serviceId) return;
        if (!prixLu) { setErreurModale('Cochez la case pour confirmer que vous avez pris connaissance du prix.'); return; }
        setEnvoi(true); setErreurModale('');
        try { window.location.href = (await organisationApi.payerModule(serviceId, m.name)).url; } catch (e) { setErreurModale(messageErreur(e)); setEnvoi(false); }
    };

    const demander = async (m: ModuleCatalogue) => {
        if (!serviceId) return;
        if (estModulePayant(m.tarif) && !prixLu) { setErreurModale('Ce module est payant : cochez la case pour confirmer que vous avez pris connaissance de son prix.'); return; }
        setEnvoi(true); setErreurModale('');
        try {
            await organisationApi.demanderModule(serviceId, m.name, motDemande, prixLu);
            remplacer({ ...m, demande: 'en_attente', reponse_demande: '' });
            setADemander(null); setMotDemande('');
            setConfirmation(`Demande envoyée pour « ${m.label} ». L’IAmani vous répondra.`);
        } catch (e) { setErreurModale(messageErreur(e)); }
        finally { setEnvoi(false); }
    };

    const cliquer = (m: ModuleCatalogue) => {
        if (!peutBasculer) {
            // Le client ne bascule rien : il demande. Possible aussi sur téléphone (rien n'est activé par ce geste).
            if (peutDemander && m.etat === 'inactif' && m.demande !== 'en_attente') { setErreurModale(''); setMotDemande(''); setPrixLu(false); setConfirmation(''); setADemander(m); }
            return;
        }
        if (mobile) return;
        if (m.etat === 'verrouille') return;
        if (m.etat === 'inactif') { setErreurModale(''); setAActiver(m); return; }
        if (!m.core) void desactiver(m);
    };

    const sousTitre = service && modules ? `${service.name} · ${nbActifs} module${nbActifs > 1 ? 's' : ''} activé${nbActifs > 1 ? 's' : ''} sur ${modules.length}` : ' ';

    return (
        <div className="space-y-4 max-w-[1100px]">
            {bandeau}
            <DashboardHeader
                societe={user?.type === 'admin' ? '' : user?.company_name || ''}
                titre="Mes modules"
                sousTitre={sousTitre}
            />
            {erreur ? <EtatEchec erreur={erreur} titre="Impossible d'afficher vos modules" onRetry={() => charger()} /> : !modules ? <Squelettes lignes={3} /> : (
                <>
                    {aide && (
                        <div className="flex items-start gap-2.5 rounded-xl bg-state-info-soft text-state-info px-4 py-3 text-[13px]">
                            <Info size={16} className="mt-0.5 shrink-0" />
                            <p className="flex-1"><b>À quoi ça sert ?</b> Un module activé apparaît dans la barre latérale du service choisi.{peutBasculer ? ' Le désactiver ne supprime rien.' : " Pour ajouter un module, cliquez sur « Demander l’activation » : L’IAmani vous répond."}</p>
                            <button type="button" aria-label="Masquer l'aide" onClick={() => setAide(false)} className="shrink-0 w-7 h-7 max-md:w-11 max-md:h-11 inline-flex items-center justify-center rounded hover:bg-black/5"><X size={14} /></button>
                        </div>
                    )}
                    {confirmation && <div role="status" className="rounded-xl bg-state-ok-soft text-state-ok px-4 py-3 text-[13px]">{confirmation}</div>}
                    {alerte && <div role="alert" className="rounded-xl bg-state-danger-soft text-state-danger px-4 py-3 text-[13px]">{alerte}</div>}
                    {!clientId && user?.type === 'client' && <MaFormuleCarte />}
                    {contexte && contexte.services.length > 1 && (
                        <Segmented className="[&>button]:min-h-[44px] md:[&>button]:min-h-0" value={serviceId || ''} ariaLabel="Service" options={contexte.services.map((s) => ({ value: s.id, label: s.name }))} onChange={(id) => { setModules(null); void charger(id); }} />
                    )}
                    <div className="flex flex-wrap gap-2" role="group" aria-label="Famille de modules">
                        {['Toutes', ...FAMILLES_MODULES].map((f) => (
                            <button key={f} type="button" aria-pressed={famille === f} onClick={() => setFamille(f)}
                                className={`h-11 md:h-8 min-w-[44px] md:min-w-0 px-3.5 rounded-full border text-[12.5px] font-semibold whitespace-nowrap ${famille === f ? 'bg-content text-surface border-transparent' : 'bg-surface text-content border-border hover:border-content'}`}>{f}</button>
                        ))}
                    </div>
                    {nbActifs === 0 && (
                        <div className="rounded-xl border border-border bg-surface px-5 py-6 text-center">
                            <div className="text-[14px] font-semibold text-content">Aucun module activé</div>
                            <p className="text-[12.5px] text-content-muted mt-1">Choisissez un module dans la grille ci-dessous pour commencer.</p>
                        </div>
                    )}
                    {groupes.map((g) => (
                        <section key={g.titre} aria-label={g.titre}>
                            <h2 className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-2 px-0.5">{g.titre}</h2>
                            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                                {g.cartes.map((m) => <CarteModule key={m.name} module={m} incline={incline === m.name} interactif={!mobile} peutBasculer={peutBasculer} peutDemander={peutDemander} onClick={() => cliquer(m)} />)}
                            </div>
                        </section>
                    ))}
                    {mobile && peutBasculer && <p className="flex items-center gap-2 text-[12.5px] text-content-muted px-1"><Lock size={13} /> L'activation d'un module se fait depuis un ordinateur.</p>}
                    {!clientId && <DroitsUsagePage sansEntete seulementLimites />}
                </>
            )}
            <ModaleDemande module={aDemander} service={service} etablissement={contexte?.etablissement.name || ''} envoi={envoi} erreur={erreurModale} message={motDemande} prixLu={prixLu} onPrixLu={setPrixLu} onMessage={setMotDemande} onClose={() => setADemander(null)} onEnvoyer={() => aDemander && void demander(aDemander)} onPayer={() => aDemander && void payer(aDemander)} />
            <ModaleActivation module={aActiver} service={service} etablissement={contexte?.etablissement.name || ''} envoi={envoi} erreur={erreurModale} onClose={() => setAActiver(null)} onActiver={() => aActiver && void activer(aActiver)} />
        </div>
    );
};

export default MesModulesPage;
